// @ts-strict-ignore
import React, { useEffect, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  Badge,
  Box,
  Button,
  Icon,
  Input,
  MultiSelectList,
  RowsSkeletonLoader,
  toast,
} from '@node-space/ui-components'
import { logSentryErrorAndGetEventId } from '@node-space/utils'
import { RequestError } from 'components/Errors/RequestError'
import { DurationInMilliseconds } from 'constants/General'
import { useCreateNotificationDestinationsMutation } from 'hooks/mutations/useCreateNotificationDestinationsMutation'
import { useUpdateNotificationDestinationMutation } from 'hooks/mutations/useUpdateNotificationDestinationMutation'
import { useNotificationEventsQuery } from 'hooks/queries/WebHooks/useNotificationEventsQuery'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { AddHookFormFields, addHookFormSchema } from 'pages/Wallets/schemas/addHookFormSchema'
import {
  PostNotificationDestinationsRequest,
  UpdateNotificationDestinationRequest,
} from 'services/WebHooksService'
import type { WebhooksRequestConfig } from 'hooks/useWebhooksBaseUrl'
import { BaseErrorResponse } from 'types/beneficiaries'
import { NotificationDestination } from '../types'

interface AddNotificationProps {
  destination?: NotificationDestination
  handleClose?: () => void
}

export const AddNotificationForm = ({ destination, handleClose }: AddNotificationProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [searchQuery, setSearchQuery] = useState('')
  const { requestError, setRequestError, resetRequestError } = useMappedErrorMessage()
  const [multiSelectItems, setMultiSelectItems] = useState([])

  const formSchema = addHookFormSchema(t)
  const { control, formState, register, setValue, clearErrors, trigger } =
    useForm<AddHookFormFields>({
      resolver: yupResolver(formSchema),
    })

  // Add new notification destination
  const { data: notificationEvents, isPending: isNotificationEventsLoading } =
    useNotificationEventsQuery()

  // Update notification destination
  const {
    mutate: updateNotificationDestination,
    isPending: isUpdateNotificationDestinationLoading,
  } = useUpdateNotificationDestinationMutation()

  const [description, url] = useWatch({
    control,
    name: ['description', 'url'],
  })

  const { mutate: addNotificationDestination, isPending: isAddNotificationDestinationLoading } =
    useCreateNotificationDestinationsMutation()

  const handleOnSuccessUpdateNotificationDestination = async () => {
    toast({
      header: destination?.id
        ? t('integrations.updateDestination')
        : t('integrations.addDestination'),
      variant: 'success',
      duration: DurationInMilliseconds.TEN_SECONDS,
    })
    await queryClient.invalidateQueries({ queryKey: ['destinations'] })
    handleClose?.()
  }

  const handleOnErrorUpdateNotificationDestination = (
    error: BaseErrorResponse,
    data:
      | Omit<UpdateNotificationDestinationRequest, keyof WebhooksRequestConfig>
      | Omit<PostNotificationDestinationsRequest, keyof WebhooksRequestConfig>
  ) => {
    const requestUrl = destination?.id
      ? `/webhooks/destinations/${destination?.id}`
      : '/webhooks/destinations'
    const requestName = destination?.id
      ? 'useUpdateNotificationDestinationMutation'
      : 'useCreateNotificationDestinationsMutation'
    const sentryEventId = logSentryErrorAndGetEventId(
      `Failed API request to ${requestUrl} with status ${error?.status} - ${requestName}`,
      error,
      {
        data: typeof data === 'string' ? { message: data } : data,
      }
    )
    setRequestError({
      errorCode: error.status,
      show: true,
      sentryTraceId: sentryEventId,
    })
  }
  const handleUpdateNotificationDestination = async () => {
    clearErrors()
    resetRequestError()
    const isFormValid = await trigger(['url', 'description'])
    if (isFormValid && destination?.id) {
      const updateRequest: Omit<UpdateNotificationDestinationRequest, keyof WebhooksRequestConfig> = {
        destinationId: destination.id,
        description,
        events: multiSelectItems?.filter(item => item?.checked)?.map(item => item?.title),
      }
      updateNotificationDestination(updateRequest, {
        onSuccess: handleOnSuccessUpdateNotificationDestination,
        onError: handleOnErrorUpdateNotificationDestination,
      })
    }
  }

  const handleAddNotificationDestination = async () => {
    clearErrors()
    resetRequestError()
    const isFormValid = await trigger(['url'])
    if (isFormValid) {
      const postRequest: Omit<PostNotificationDestinationsRequest, keyof WebhooksRequestConfig> = {
        url,
        description,
        type: 'WEB',
        events: multiSelectItems?.filter(item => item?.checked)?.map(item => item?.title),
        signingMode: 'HMAC',
      }
      addNotificationDestination(postRequest, {
        onSuccess: handleOnSuccessUpdateNotificationDestination,
        onError: (error, data) => handleOnErrorUpdateNotificationDestination(error, data),
      })
    }
  }

  const handleAddOrUpdateNotificationDestination = async () => {
    if (destination?.id) {
      handleUpdateNotificationDestination()
    } else {
      handleAddNotificationDestination()
    }
  }
  useEffect(() => {
    if (notificationEvents) {
      const { eventCodes = [] } = destination || {}

      const newMultiSelectItems = notificationEvents?.map((event, index) => ({
        id: String(index),
        title: event?.code,
        checked: eventCodes?.includes(event?.code),
      }))
      setMultiSelectItems(newMultiSelectItems)
    }

    if (destination) {
      setValue('description', destination?.description)
      setValue('url', destination?.url)
    }
  }, [notificationEvents, destination])

  const isAddingNotificationEnabled =
    !!url && !!description && multiSelectItems?.some(item => item?.checked)

  const filteredItems = searchQuery
    ? multiSelectItems?.filter(item =>
      item?.title?.toLowerCase()?.includes?.(searchQuery.toLowerCase())
    )
    : multiSelectItems

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event?.target?.value)
  }

  const handleSelectAllChange = updatedSections => {
    const updatedItems = updatedSections?.[0]?.items || []

    if (!updatedItems?.length) {
      return
    }

    setMultiSelectItems(updatedItems)
  }

  const hasCheckedItems = multiSelectItems?.filter?.(item => item?.checked)?.length > 0

  return (
    <Box paddingX={24} paddingT={24} width="full" className="space-y-4">
      {requestError?.show && (
        <RequestError
          errorCode={requestError?.errorCode}
          sentryTraceId={requestError?.sentryTraceId}
        />
      )}

      <Input
        {...register('description')}
        label={t('integrations.description')}
        inputMode="text"
        name="description"
        testId="description"
        errorText={formState?.errors?.description?.message}
        error={!!formState?.errors?.description}
        onChange={e => setValue('description', e?.target?.value)}
        value={description}
      />

      <Input
        {...register('url')}
        label={t('integrations.webhookURLoReceiveEvents')}
        placeholder={t('integrations.enterURL')}
        inputMode="text"
        name="url"
        testId="url"
        errorText={formState?.errors?.url?.message}
        error={!!formState?.errors?.url}
        onChange={e => setValue('url', e?.target?.value)}
        value={url}
        disabled={!!destination?.id}
      />

      <Box paddingB={16} width="full">
        <Box paddingB={8} width="full" className="relative">
          <Input
            label={t('integrations.eventsToSend')}
            placeholder={t('integrations.search')}
            inputMode="text"
            name="eventsToSend"
            testId="eventsToSend"
            IconButton={
              <Button
                variant="ghost"
                disabled
                noPadding
                iconElement={<Icon name="MagnifyingGlass" />}
              />
            }
            value={searchQuery}
            onChange={handleSearchChange}
          />
          <Box className="absolute top-[60%] right-0 -translate-y-[140%] translate-x-[45%]">
            {hasCheckedItems && (
              <Badge>{multiSelectItems?.filter?.(item => item?.checked)?.length}</Badge>
            )}
          </Box>
        </Box>
        {isNotificationEventsLoading ? (
          <Box flex flexCol justify="center" paddingT={12} data-testid="loader">
            <RowsSkeletonLoader rows={6} />
          </Box>
        ) : (
          <MultiSelectList
            multiSelectListDetails={{
              testId: 'integrations',
              headerLabel: t('integrations.selectAllEvents'),
              onSelectAllChange: handleSelectAllChange,
              noSectionItemsMessage: t('integrations.noItemsAvailable'),
              sections: [
                {
                  id: '1',
                  title: t('integrations.events'),
                  description: `${filteredItems?.length} Items`,
                  items: filteredItems,
                },
              ],
            }}
          />
        )}
      </Box>
      <Box className="flex justify-between">
        <Button variant="secondary" onClick={handleClose}>
          {t('cancel')}
        </Button>
        <Button
          loading={isAddNotificationDestinationLoading || isUpdateNotificationDestinationLoading}
          onClick={handleAddOrUpdateNotificationDestination}
          disabled={!isAddingNotificationEnabled}
        >
          {destination?.id ? t('integrations.update') : t('integrations.add')}
        </Button>
      </Box>
    </Box>
  )
}
